import React, { useState, useMemo } from 'react';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { Table, Input, Spin, Alert, Row, Col, Modal, Descriptions, Button, Tag, Space, Card, Tooltip, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  useListServersQuery, useRestartServerMutation, useMarkPaidMutation, useResolveIssueMutation,
} from '../store/api';
import {
  PlusOutlined, ReloadOutlined, EyeOutlined, EditOutlined,
  SyncOutlined, DollarOutlined, CheckOutlined, CodeOutlined,
  SearchOutlined, CloudServerOutlined,
} from '@ant-design/icons';
import UpdateServersButton from '../components/UpdateServersButton';

const { Paragraph } = Typography;

const renderDescripcionDetallada = (text) => {
  if (!text || !String(text).trim()) {
    return <span style={{ color: '#94a3b8', fontSize: 12 }}>Sin descripción</span>;
  }
  const lines = String(text).split('\n').filter(l => l.trim());
  const preview = lines.slice(0, 3).map(l => l.replace(/^•\s*/, '')).join(' · ');
  const fullContent = (
    <div style={{ maxWidth: 420, maxHeight: 360, overflowY: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{ marginBottom: 4, fontSize: 12, lineHeight: 1.5 }}>{line}</div>
      ))}
    </div>
  );
  return (
    <Tooltip title={fullContent} placement="topLeft" color="#1e293b">
      <Paragraph ellipsis={{ rows: 3, expandable: false }} style={{ margin: 0, fontSize: 12, color: '#475569', maxWidth: 300 }}>
        {preview}{lines.length > 3 ? '…' : ''}
      </Paragraph>
    </Tooltip>
  );
};

const Lista = () => {
  const { data = [], isLoading, error, refetch } = useListServersQuery(undefined, { pollingInterval: 60000 });
  const [restartServer] = useRestartServerMutation();
  const [markPaid] = useMarkPaidMutation();
  const [resolveIssue] = useResolveIssueMutation();

  const [search, setSearch] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [modalRecord, setModalRecord] = useState(null);
  const navigate = useNavigate();

  const handleAction = async (type, record) => {
    const actions = {
      reiniciar: { text: '¿Reiniciar el servidor?', fn: () => restartServer(record.id).unwrap() },
      pagado: { text: '¿Confirmar pago del servidor?', fn: () => markPaid(record.id).unwrap() },
      resuelto: { text: '¿Marcar problema como resuelto?', fn: () => resolveIssue(record.id).unwrap() },
    };
    const action = actions[type];
    if (!action) return;

    const result = await Swal.fire({
      title: action.text, icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Sí', cancelButtonText: 'No',
      customClass: { popup: 'modern-swal' },
    });

    if (result.isConfirmed) {
      try {
        await action.fn();
        Swal.fire('Completado', 'La operación se realizó con éxito.', 'success');
      } catch {
        Swal.fire('Error', 'No se pudo ejecutar la acción.', 'error');
      }
    }
  };

  const columns = useMemo(() => [
    {
      title: 'Servidor', dataIndex: 'nombre', key: 'nombre', fixed: 'left', width: 280,
      render: (_, record) => {
        const lastUpdate = record.fechaupdatedsincro ? dayjs(record.fechaupdatedsincro) : null;
        const isOnline = lastUpdate && dayjs().diff(lastUpdate, 'minute') <= 5;
        let dominiosList = [];
        try { dominiosList = record.dominios ? JSON.parse(record.dominios) : []; } catch { dominiosList = []; }

        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span className={`status-dot ${isOnline ? 'online' : 'offline'}`} />
              <span style={{ fontWeight: 600, fontSize: 14 }}>{record.nombre}</span>
            </div>
            {record.ip && (
              <span style={{ background: '#f1f5f9', color: '#475569', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontFamily: 'monospace' }}>
                {record.ip}
              </span>
            )}
            <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {record.ram && <Tag color="blue" style={{ fontSize: 10, margin: 0, borderRadius: 6, lineHeight: '18px' }}>{record.ram} RAM</Tag>}
              {record.proveedor && <Tag color="green" style={{ fontSize: 10, margin: 0, borderRadius: 6, lineHeight: '18px' }}>{record.proveedor}</Tag>}
              {record.nucleos && <Tag color="orange" style={{ fontSize: 10, margin: 0, borderRadius: 6, lineHeight: '18px' }}>{record.nucleos} núcleos</Tag>}
            </div>
            {(record.total_dominios || 0) > 0 && (
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #f0f0f0' }}>
                <Tag color="geekblue" style={{ fontSize: 10, borderRadius: 6 }}>{record.total_dominios} dominios</Tag>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 3 }}>
                  {dominiosList.slice(0, 5).map((d, i) => (
                    <Tag key={i} color="default" style={{ fontSize: 9, margin: 0, padding: '0 5px', lineHeight: '16px' }}>{d}</Tag>
                  ))}
                  {dominiosList.length > 5 && <Tag style={{ fontSize: 9, margin: 0 }}>+{dominiosList.length - 5}</Tag>}
                </div>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Descripción', dataIndex: 'descripcion_detallada', key: 'descripcion_detallada', width: 320,
      render: (text) => renderDescripcionDetallada(text),
    },
    {
      title: 'Latencia', dataIndex: 'latencia', key: 'latencia', width: 100,
      sorter: (a, b) => (a.latencia || 999999) - (b.latencia || 999999),
      render: (latencia) => {
        if (!latencia) return <Tag>N/A</Tag>;
        const color = latencia > 300 ? '#f43f5e' : latencia > 100 ? '#f59e0b' : '#10b981';
        return <span className="status-badge" style={{ background: `${color}15`, color }}>{latencia}ms</span>;
      },
    },
    {
      title: 'Carga', dataIndex: 'carga', key: 'carga', width: 100,
      sorter: (a, b) => parseFloat(a.carga) - parseFloat(b.carga),
      render: (carga) => {
        const val = parseFloat(String(carga).split(',')[0]) || 0;
        const color = val > 4 ? '#f43f5e' : val > 2 ? '#f59e0b' : '#10b981';
        return <span className="status-badge" style={{ background: `${color}15`, color, fontWeight: 700 }}>{carga}</span>;
      },
    },
    {
      title: 'Espacio', dataIndex: 'espaciolibre', key: 'espaciolibre', width: 160,
      render: (espaciolibre, record) => {
        const parseGB = val => { if (!val) return 0; const m = String(val).replace(',', '.').match(/([\d.]+)/); return m ? parseFloat(m[1]) : 0; };
        const total = parseGB(record.disco);
        const libre = parseGB(espaciolibre);
        const percent = total > 0 ? Math.round((libre / total) * 100) : 0;
        const color = percent > 30 ? '#10b981' : percent > 10 ? '#f59e0b' : '#f43f5e';
        return (
          <div style={{ minWidth: 120 }}>
            <div style={{ fontSize: 12, marginBottom: 4, color: '#475569' }}>{libre}GB / {total}GB</div>
            <div className="mini-progress"><div className="mini-progress-bar" style={{ width: `${percent}%`, background: color }} /></div>
            <div style={{ fontSize: 11, color, fontWeight: 600, marginTop: 2 }}>{percent}% libre</div>
          </div>
        );
      },
    },
    {
      title: 'Últ. Sincro', dataIndex: 'fechaupdatedsincro', key: 'fechaupdatedsincro', width: 140,
      render: (fecha) => {
        if (!fecha) return <Tag>N/A</Tag>;
        const diffMin = dayjs().diff(dayjs(fecha), 'minute');
        const isOk = diffMin <= 5;
        return (
          <span className={`status-badge ${isOk ? 'online' : 'offline'}`}>
            <span className={`status-dot ${isOk ? 'online' : 'offline'}`} />
            {dayjs(fecha).format('HH:mm:ss')}
          </span>
        );
      },
    },
    {
      title: 'Próx. pago', dataIndex: 'proximopago', key: 'proximopago', width: 120,
      sorter: (a, b) => (a.proximopago || '').localeCompare(b.proximopago || ''),
      render: (fecha, record) => {
        if (!fecha || fecha === '0000-00-00') {
          return <Tag color="default">—</Tag>;
        }
        const d = dayjs(fecha);
        const prov = String(record.proveedor || '').toUpperCase();
        const isContabo = prov.includes('CONTABO');
        const isHetzner = prov.includes('HETZNER') || prov.includes('HEZTNER');
        const overdue = d.isBefore(dayjs(), 'day');
        const soon = !overdue && d.diff(dayjs(), 'day') <= 5;
        const color = overdue ? 'red' : soon ? 'orange' : 'green';
        const apiTitle = isContabo ? 'API Contabo' : isHetzner ? 'API Hetzner Cloud (pricing + servers)' : undefined;
        return (
          <Tag color={color} title={apiTitle}>
            {d.format('DD/MM/YYYY')}
          </Tag>
        );
      },
    },
    {
      title: 'Error', dataIndex: 'error', key: 'error', width: 80,
      render: (err) => {
        const hasError = err && String(err).trim() && String(err).trim() !== 'NA';
        return hasError ? <Tag color="red" style={{ borderRadius: 12 }}>Error</Tag> : <Tag color="green" style={{ borderRadius: 12 }}>OK</Tag>;
      },
    },
    {
      title: 'Acciones', key: 'acciones', width: 200, fixed: 'right',
      render: (_, record) => {
        const isError = record.error && String(record.error).trim() && String(record.error).trim() !== 'NA';
        const canPagado = record.proximopago ? dayjs(record.proximopago).isBefore(dayjs()) : false;
        return (
          <Space size={4}>
            <button className="action-btn" title="Ver" onClick={() => { setModalRecord(record); setModalVisible(true); }}><EyeOutlined /></button>
            <button className="action-btn" title="Editar" onClick={() => navigate(`/editar/${record.id}`)}><EditOutlined /></button>
            <button className="action-btn" title="Reiniciar" onClick={() => handleAction('reiniciar', record)}><SyncOutlined /></button>
            <button className="action-btn" title="Pagado" disabled={!canPagado} onClick={() => handleAction('pagado', record)}><DollarOutlined /></button>
            <button className="action-btn" title="Resuelto" disabled={!isError} onClick={() => handleAction('resuelto', record)}><CheckOutlined /></button>
            <button className="action-btn" title="Remoto" onClick={() => record.ip && window.open(`vscode://vscode-remote/ssh-remote+root@${record.ip.replace(/\s|\t|\n/g, '')}`)}><CodeOutlined /></button>
          </Space>
        );
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [navigate]);

  const filteredData = useMemo(() => {
    if (!search) return data;
    const lower = search.toLowerCase();
    return data.filter(item => Object.values(item).some(val => String(val).toLowerCase().includes(lower)));
  }, [data, search]);

  if (isLoading) {
    return (
      <div className="page-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="page-container fade-in">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title"><CloudServerOutlined style={{ marginRight: 8 }} />Servidores</h1>
          <div className="page-subtitle">{data.length} servidores registrados</div>
        </div>
        <Space>
          <UpdateServersButton onUpdate={refetch} />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Actualizar</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/agregar')}
            style={{ borderRadius: 8, fontWeight: 600, background: 'linear-gradient(135deg, #845adf, #6c3fc5)', border: 'none' }}>
            Agregar Servidor
          </Button>
        </Space>
      </div>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Input prefix={<SearchOutlined style={{ color: '#94a3b8' }} />} placeholder="Buscar servidor..."
            value={search} onChange={e => setSearch(e.target.value)} allowClear style={{ borderRadius: 8 }} />
        </Col>
      </Row>

      {error && <Alert message={error?.data?.message || 'Error al obtener datos'} type="error" showIcon style={{ marginBottom: 16, borderRadius: 8 }} />}

      <Card bordered={false} bodyStyle={{ padding: 0 }}>
        <div className="modern-table">
          <Table columns={columns} dataSource={filteredData} rowKey="id" scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 20, position: ['bottomRight'], showSizeChanger: true, showTotal: (total) => `${total} servidores` }} size="middle" />
        </div>
      </Card>

      <Modal title="Detalles del Servidor" open={modalVisible}
        onCancel={() => { setModalVisible(false); setModalRecord(null); }}
        footer={<Button onClick={() => { setModalVisible(false); setModalRecord(null); }}>Cerrar</Button>} width={640}>
        {modalRecord && (
          <>
            {modalRecord.descripcion_detallada && (
              <div style={{ marginBottom: 16, padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: 600, marginBottom: 8, color: '#334155' }}>Descripción operativa</div>
                {String(modalRecord.descripcion_detallada).split('\n').map((line, i) => (
                  <div key={i} style={{ fontSize: 13, lineHeight: 1.6, color: '#475569' }}>{line}</div>
                ))}
              </div>
            )}
            <Descriptions bordered column={1} size="small">
              {Object.entries(modalRecord)
                .filter(([key]) => !['dominios', 'descripcion_detallada'].includes(key))
                .map(([key, value]) => (
                  <Descriptions.Item key={key} label={<span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{key}</span>}>
                    {String(value)}
                  </Descriptions.Item>
                ))}
            </Descriptions>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Lista;
